import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, endpoints } from "../lib/api";
import Topbar, { PrimaryAction } from "../components/Topbar";
import { ClientStatusBadge } from "../components/StatusBadge";
import { euro, initials, shortDate } from "../lib/format";
import { Search, Sparkles } from "lucide-react";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUSES = ["todos", "nuevo", "recurrente", "vip", "inactivo"];
const MEMBERSHIPS = ["todos", "silver", "gold", "black"];

export default function Clients() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("todos");
  const [membership, setMembership] = useState("todos");
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients", q, status, membership],
    queryFn: () =>
      api.get(endpoints.clients, { params: { q, status, membership } }).then((r) => r.data),
  });

  const createMut = useMutation({
    mutationFn: (payload) => api.post(endpoints.clients, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["metrics"] });
      toast.success("Cliente creada con éxito");
      setCreateOpen(false);
    },
    onError: () => toast.error("No se pudo crear la clienta"),
  });

  return (
    <div>
      <Topbar
        title="Clientes"
        subtitle="Gestiona la ficha, historial y estado comercial de cada clienta LAKA."
        actions={<PrimaryAction label="Nueva clienta" onClick={() => setCreateOpen(true)} testId="btn-new-client" />}
      />

      {/* Filters */}
      <div className="laka-card p-4 flex flex-col md:flex-row md:items-center gap-3 mb-6" data-testid="clients-filters">
        <div className="flex items-center gap-2 flex-1 border rounded-md px-3 laka-hairline">
          <Search size={16} strokeWidth={1.25} style={{ color: "var(--laka-text)" }} />
          <Input
            data-testid="clients-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, email o teléfono…"
            className="border-0 shadow-none focus-visible:ring-0 h-10 px-0"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-44" data-testid="clients-filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s === "todos" ? "Todos los estados" : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={membership} onValueChange={setMembership}>
          <SelectTrigger className="w-full md:w-44" data-testid="clients-filter-membership">
            <SelectValue placeholder="Membresía" />
          </SelectTrigger>
          <SelectContent>
            {MEMBERSHIPS.map((m) => (
              <SelectItem key={m} value={m}>{m === "todos" ? "Todas las membresías" : "LAKA " + m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="laka-card overflow-hidden" data-testid="clients-table-card">
        <table className="w-full">
          <thead>
            <tr className="text-left">
              {["Clienta", "Membresía", "Estado", "Visitas", "Total gastado", "Última visita"].map((h) => (
                <th key={h} className="px-6 py-4 text-[10px] tracking-[0.18em] uppercase font-medium" style={{ color: "var(--laka-text)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-sm" style={{ color: "var(--laka-text)" }}>Cargando…</td></tr>
            )}
            {!isLoading && clients.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-sm" style={{ color: "var(--laka-text)" }}>Sin resultados. Ajusta los filtros o crea una nueva clienta.</td></tr>
            )}
            {clients.map((c) => (
              <tr
                key={c.id}
                data-testid={`client-row-${c.id}`}
                onClick={() => setSelectedId(c.id)}
                className="border-t laka-hairline cursor-pointer hover:bg-[#FBF6F2] transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {c.avatar ? (
                      <img src={c.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-medium" style={{ backgroundColor: "#E8DFD1", color: "#1A1A1A" }}>
                        {initials(c.name)}
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs" style={{ color: "var(--laka-text)" }}>{c.email || c.phone || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  {c.membership_plan ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: membershipColor(c.membership_plan) }} />
                      LAKA {c.membership_plan.charAt(0).toUpperCase() + c.membership_plan.slice(1)}
                    </span>
                  ) : (
                    <span style={{ color: "var(--laka-text)" }}>Sin membresía</span>
                  )}
                </td>
                <td className="px-6 py-4"><ClientStatusBadge status={c.status} /></td>
                <td className="px-6 py-4 text-sm font-medium">{c.visits}</td>
                <td className="px-6 py-4 text-sm font-medium">{euro(c.total_spent)}</td>
                <td className="px-6 py-4 text-sm" style={{ color: "var(--laka-text)" }}>{shortDate(c.last_visit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ClientDetailPanel
        clientId={selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={() => {
          qc.invalidateQueries({ queryKey: ["clients"] });
        }}
      />

      <NewClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(payload) => createMut.mutate(payload)}
        loading={createMut.isPending}
      />
    </div>
  );
}

function membershipColor(p) {
  return { silver: "#B8B8B8", gold: "#D6B36A", black: "#1A1A1A" }[p] || "#4A4A4A";
}

function ClientDetailPanel({ clientId, onClose, onUpdated }) {
  const qc = useQueryClient();
  const { data: client } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => api.get(endpoints.client(clientId)).then((r) => r.data),
    enabled: !!clientId,
  });
  const { data: history } = useQuery({
    queryKey: ["client-history", clientId],
    queryFn: () => api.get(endpoints.clientHistory(clientId)).then((r) => r.data),
    enabled: !!clientId,
  });

  const updateMut = useMutation({
    mutationFn: (payload) => api.put(endpoints.client(clientId), payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client", clientId] });
      onUpdated?.();
      toast.success("Ficha actualizada");
    },
  });

  const [notes, setNotes] = useState("");

  return (
    <Sheet open={!!clientId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="client-detail-panel">
        {client && (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-3xl">Ficha de clienta</SheetTitle>
            </SheetHeader>

            <div className="mt-6 flex items-center gap-4">
              {client.avatar ? (
                <img src={client.avatar} alt="" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-medium" style={{ backgroundColor: "#E8DFD1" }}>
                  {initials(client.name)}
                </div>
              )}
              <div className="flex-1">
                <div className="font-display text-2xl leading-tight">{client.name}</div>
                <div className="text-xs mt-1" style={{ color: "var(--laka-text)" }}>{client.email || "—"} · {client.phone || "—"}</div>
                <div className="mt-2 flex gap-2 items-center flex-wrap">
                  <ClientStatusBadge status={client.status} />
                  {client.membership_plan && (
                    <span className="text-[11px] px-3 py-0.5 rounded-full" style={{ backgroundColor: "#F5EFE7", color: "#1A1A1A" }}>
                      LAKA {client.membership_plan.charAt(0).toUpperCase() + client.membership_plan.slice(1)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-6">
              <MiniStat label="Visitas" value={client.visits} />
              <MiniStat label="Total gastado" value={euro(client.total_spent)} />
              <MiniStat label="Última visita" value={shortDate(client.last_visit)} />
            </div>

            <SectionTitle>Preferencias</SectionTitle>
            <p className="text-sm" style={{ color: "var(--laka-text)" }}>
              {client.preferences || "Sin preferencias registradas."}
            </p>

            <SectionTitle>Notas internas</SectionTitle>
            <Textarea
              data-testid="client-notes-textarea"
              defaultValue={client.notes || ""}
              placeholder="Añadir notas internas sobre la clienta…"
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => updateMut.mutate({ notes })}
              className="min-h-[80px]"
            />

            <SectionTitle>Cambiar estado</SectionTitle>
            <Select
              value={client.status}
              onValueChange={(v) => updateMut.mutate({ status: v })}
            >
              <SelectTrigger className="w-full" data-testid="client-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nuevo">Nueva</SelectItem>
                <SelectItem value="recurrente">Recurrente</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
                <SelectItem value="inactivo">Inactiva</SelectItem>
              </SelectContent>
            </Select>

            <SectionTitle>Historial de tratamientos</SectionTitle>
            <div className="space-y-2">
              {(!history || history.appointments.length === 0) && (
                <div className="text-sm" style={{ color: "var(--laka-text)" }}>Sin historial.</div>
              )}
              {history?.appointments.slice(0, 8).map((a) => (
                <div key={a.id} className="flex items-center justify-between text-sm py-2 border-b laka-hairline last:border-0">
                  <div>
                    <div className="font-medium">{a.service_name}</div>
                    <div className="text-xs" style={{ color: "var(--laka-text)" }}>{shortDate(a.starts_at)} · {a.professional}</div>
                  </div>
                  <div className="text-xs" style={{ color: a.status === 'completada' ? "#2D6A4F" : "var(--laka-text)" }}>
                    {a.status}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border rounded-md p-3 laka-hairline">
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--laka-text)" }}>{label}</div>
      <div className="font-display text-xl mt-1 leading-none">{value}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="mt-8 mb-3 laka-eyebrow">{children}</div>;
}

function NewClientDialog({ open, onOpenChange, onSubmit, loading }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", status: "nuevo", membership_plan: "" });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    onSubmit({
      ...form,
      membership_plan: form.membership_plan || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Nueva clienta</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label>Nombre completo *</Label>
            <Input data-testid="new-client-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ej: Marta Sánchez" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input data-testid="new-client-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="marta@mail.com" />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input data-testid="new-client-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+34 600 000 000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="new-client-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nuevo">Nueva</SelectItem>
                  <SelectItem value="recurrente">Recurrente</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                  <SelectItem value="inactivo">Inactiva</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Membresía</Label>
              <Select value={form.membership_plan} onValueChange={(v) => set("membership_plan", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="new-client-plan"><SelectValue placeholder="Sin membresía" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin membresía</SelectItem>
                  <SelectItem value="silver">LAKA Silver</SelectItem>
                  <SelectItem value="gold">LAKA Gold</SelectItem>
                  <SelectItem value="black">LAKA Black</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="new-client-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={loading} className="laka-btn-primary" data-testid="new-client-submit">
            <Sparkles size={14} strokeWidth={1.5} className="mr-1" />
            Crear clienta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
